import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
} from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../common/components/base.component';
import { AlertifyService } from '../../../../services/alertify.service';
import { HubApiService } from '../../../../services/hub.api.service';
import { HubComponent } from '../../hub.component';
import { ButtonTriggerEvent } from '../../../../common/components';
import { EventLocationFilterGroup, PlaceLocationFilterGroup } from './location.filter.settings.provider';
import { AccountApiService } from '../../../../services/account.api.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ShowInfoModalComponent } from '../../show.info.modal/show.info.modal.component';
import { InfoModalData, ShowInfoModalActions } from '../../show.info.modal/show.info.modal.provider';
import { ModalService } from '../../../../common/services/modal.service';
import { HubVisitorFeatureToggle, HubVisitorToggleDataSource } from '../../../../models/hub/hub.visitor.feature.toggle';
import { ToggleAndDataSourceStore } from '../../../../stores/hub.calendar.store';

@Component({
  selector: 'ig-location-filters',
  templateUrl: './location.filter.settings.component.html',
  styleUrls: ['./location.filter.settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LocationFilterSettingsComponent extends BaseComponent implements OnInit {
  @Input() show: 'editor' | 'custom' | 'promotion';
  public toggles = new HubVisitorFeatureToggle();
  public hubName = null;

  public clientPlaceGroups: PlaceLocationFilterGroup[] = [];
  public clientEventGroups: EventLocationFilterGroup[] = [];
  public selectedPlaceGroup: PlaceLocationFilterGroup = null;
  public selectedEventGroup: EventLocationFilterGroup = null;
  public placeCities: string[] = [];
  public placeIsCityLevelFilter: boolean = false;
  public eventCities: string[] = [];
  public eventIsCityLevelFilter: boolean = false;

  public form: FormGroup;

  constructor(private formBuilder: FormBuilder,
              private hubApiService: HubApiService,
              private parent: HubComponent,
              private activatedRoute: ActivatedRoute,
              private alertify: AlertifyService,
              private accountApiService: AccountApiService,
              private toggleAndDataSourceStore: ToggleAndDataSourceStore,
              private modalService: ModalService,
  ) {
    super({activatedRoute});
  }

  public ngOnInit() {
    let {hubName} = this.allRouteParams;
    this.hubName = hubName;
    this.parent.location = 'Location Filters';

    this.form = this.formBuilder.group({
      tempTitle: [null, []],
    });

    this.toggleAndDataSourceStore.toggleAndDataSourceCommand
      .takeUntil(this.isDestroyed)
      .subscribe((toggleCommand: HubVisitorToggleDataSource) => {
        if (toggleCommand.toggle) {
          this.toggles = toggleCommand.toggle;
        }
      });

    this.loadLocations();
  }

  public loadLocations() {
    this.clientEventGroups = [];
    this.clientPlaceGroups = [];
    this.accountApiService.getCustomLocationFilterGroups(this.hubName)
      .subscribe((res) => {
        if (res && res.customPlaceLocationFilterGroups) {
          this.clientPlaceGroups = res.customPlaceLocationFilterGroups.placeGroups;
          this.placeIsCityLevelFilter = res.customPlaceLocationFilterGroups.isCityLevelFilter;
        }
        if (res && res.customEventLocationFilterGroups) {
          this.clientEventGroups = res.customEventLocationFilterGroups.eventGroups;
          this.eventIsCityLevelFilter = res.customEventLocationFilterGroups.isCityLevelFilter;
        }
      });

    this.placeCities = [];
    this.hubApiService.getPlaceLocationFilters(this.hubName)
      .subscribe((localities) => {
        if (localities && localities.length > 0) {
          localities.forEach((item) => {
            this.placeCities.push(item.locality);
          });
        }
      });

    this.eventCities = [];
    this.hubApiService.getEventLocationFilters(this.hubName)
      .subscribe((localities) => {
        if (localities && localities.length > 0) {
          localities.forEach((item) => {
            this.eventCities.push(item.locality);
          });
        }
      });
  }

  public get isEventLocationFilterDisabled() {
    return !this.toggles.eventLocationFilter || (!this.toggles.eventByGeolocation && !this.toggles.manualEvents);
  }

  public addPlaceGroup() {
    this.clientPlaceGroups.push(new PlaceLocationFilterGroup('New group'));
  }

  public addEventGroup() {
    this.clientEventGroups.push(new EventLocationFilterGroup('New group'));
  }

  public addCityToPlaceGroup($event, city) {
    if (!this.selectedPlaceGroup) return;

    if ($event.checked) {
      if (!this.selectedPlaceGroup.cities.any((item) => item === city)) {
        this.selectedPlaceGroup.cities.push(city);
      }
    } else {
      this.selectedPlaceGroup.cities = this.selectedPlaceGroup.cities.filter((item) => item !== city);
    }
  }

  public addCityToEventGroup($event, city) {
    if (!this.selectedEventGroup) return;

    if ($event.checked) {
      if (!this.selectedEventGroup.cities.any((item) => item === city)) {
        this.selectedEventGroup.cities.push(city);
      }
    } else {
      this.selectedEventGroup.cities = this.selectedEventGroup.cities.filter((item) => item !== city);
    }
  }

  public save(event: ButtonTriggerEvent) {
    console.log(this.clientPlaceGroups);
    if (this.clientPlaceGroups.any((item) => !item.cities.length)) {
      this.alertify.warning('Please select cities for all groups');
      event.immediateComplete();
      return;
    }

    this.accountApiService
      .setCustomPlaceGroups(
        this.hubName,
        this.clientPlaceGroups,
        this.placeIsCityLevelFilter
      )
      .finally(event.complete)
      .subscribe(() => {
        this.alertify.successfully('Set place groups for location filter');
      });
  }

  public saveEvent(event: ButtonTriggerEvent) {
    console.log(this.clientEventGroups);
    if (this.clientEventGroups.any((item) => !item.cities.length)) {
      this.alertify.warning('Please select cities for all groups');
      event.immediateComplete();
      return;
    }

    this.accountApiService
      .setCustomEventGroups(
        this.hubName,
        this.clientEventGroups,
        this.eventIsCityLevelFilter
      )
      .finally(event.complete)
      .subscribe(() => {
        this.alertify.successfully('Set event groups for location filter');
        this.loadLocations();
      });
  }

  public enterEditMode(group: PlaceLocationFilterGroup) {
    group.tempTitle = group.title;
    group.isEditing = true;
  }

  public removePlaceGroup(group: PlaceLocationFilterGroup) {
    this.clientPlaceGroups = this.clientPlaceGroups.filter((item) => item !== group);
  }

  public removeEventGroup(group: EventLocationFilterGroup) {
    this.clientEventGroups = this.clientEventGroups.filter((item) => item !== group);
  }

  public updateGroupTitle(group: PlaceLocationFilterGroup) {
    if (group.tempTitle) {
      group.title = group.tempTitle;
      group.isEditing = false;
    }
  }

  public cancelGroupTitle(group: PlaceLocationFilterGroup) {
    group.tempTitle = "";
    group.isEditing = false;
  }

  public selectPlaceGroup(group: PlaceLocationFilterGroup) {
    this.clientPlaceGroups.forEach((item) => {
      item.isSelected = item === group;
    });
    this.selectedPlaceGroup = group;
  }

  public selectEventGroup(group: PlaceLocationFilterGroup) {
    this.clientEventGroups.forEach((item) => {
      item.isSelected = item === group;
    });
    this.selectedEventGroup = group;
  }

  public isCityInGroup(city, group) {
    if (!group) return false;

    return group.cities.any(x => x === city);
  }

  public setUseGroupFilter() {
    this.accountApiService
      .setCustomPlaceUseGroupFilter(
        this.hubName,
        this.placeIsCityLevelFilter
      )
      .subscribe(() => {
        if (this.placeIsCityLevelFilter) {
          this.alertify.successfully('Set use group filter');
        } else {
          this.alertify.successfully('Set use city filter');
        }
      });
  }

  public showLocationWidgetUrl(group: PlaceLocationFilterGroup, type: string) {
    if (!group._id) {
      this.alertify.warning(`Please save the changes first.`);
      return;
    }
    const link = `<div id="imgoingcalendar-wrapper" route="/${this.hubName}/${type}/location/group/${group._id}" url-change="true" version="V3">
        <app-root></app-root> 
        <script type="text/javascript" src="https://imgoingcalendar.com/imgoingscript.js"></script>
    </div>`;

    this.modalService.show({
      component: ShowInfoModalComponent,
      providers: [
        {provide: ShowInfoModalActions, useValue: null},
        {
          provide: InfoModalData,
          useValue: new InfoModalData('Place Location Widget URL', null, [], link, true),
        },
      ],
      panelClass: 'width-modal-70p',
    });
  }
}




// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/customization/location.filter.settings/location.filter.settings.component.ts